import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listProjects, deleteProject } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, ArrowRight, Building, Calendar, Loader2, Plus } from "lucide-react";
import { fmtDate } from "@/lib/corpsColors";

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    listProjects()
      .then(setItems)
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Supprimer ce planning ?")) return;
    await deleteProject(id);
    toast.success("Planning supprimé");
    load();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="history-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono-tech text-xs uppercase tracking-wider text-slate-500">
            Vos plannings
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F2137] mt-1">
            Historique
          </h1>
        </div>
        <Link to="/nouveau">
          <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0F2137] font-semibold">
            <Plus className="w-4 h-4 mr-1.5" />
            Nouveau
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#0F2137]" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-12 text-center" data-testid="empty-state">
          <Building className="w-10 h-10 text-slate-300 mx-auto" />
          <h3 className="font-display font-semibold text-lg text-[#0F2137] mt-4">
            Aucun planning pour le moment
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Créez votre premier planning en quelques clics.
          </p>
          <Link to="/nouveau">
            <Button className="mt-5 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F2137] font-semibold">
              Créer un planning
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {items.map((p) => (
            <Link
              key={p.id}
              to={`/projet/${p.id}`}
              data-testid={`project-card-${p.id}`}
              className="group rounded-xl border border-slate-200 bg-white p-5 hover:border-[#F59E0B] transition-colors block"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-semibold text-[#0F2137] truncate">{p.name}</h3>
                  <div className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-500 mt-1">
                    {p.project_type} · {p.tasks?.length || 0} tâches
                  </div>
                </div>
                <button
                  onClick={(e) => remove(p.id, e)}
                  data-testid={`delete-${p.id}`}
                  className="text-slate-300 hover:text-red-500 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-4 space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar className="w-3.5 h-3.5" />
                  {fmtDate(new Date(p.start_date + "T00:00:00"))}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#0F2137] text-white hover:bg-[#0F2137]">
                    {p.total_duration_days} jours
                  </Badge>
                  {p.surface_m2 && (
                    <Badge variant="secondary">{p.surface_m2} m²</Badge>
                  )}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-1 text-sm text-[#B45309] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Ouvrir <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
